import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type PresenceWireItem, type SupervisorRespaldoWire } from '../lib/api-client';

import { useCapability } from './use-capability';

/**
 * usePresence — Fatia G (ADR-049 + Caderno cap 39).
 *
 * Lista presence dos atendentes do tenant + mutation para atualizar próprio
 * status. Refresh em WS event `peer-coordination.presence.changed`.
 */
export function usePresence(): {
  data: PresenceWireItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['presence-team'],
    queryFn: async () => {
      const res = await api.getTeamPresence();
      return res.data;
    },
    staleTime: 30 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * useMyTeamPresence — Item 365 (faixa "Colegas do meu time" do Painel Pessoal).
 *
 * Presença restrita aos colegas dos times do caller (`teamScope=mine`, resolvido
 * server-side). Query GATEADA por `peer-coordination-presence` no `enabled` (sem a
 * cap o fetch nem dispara — "gatear o fetch, não só o render"). Refresh em WS
 * `peer-coordination.presence.changed` (reusa a invalidação de presence-team).
 */
export function useMyTeamPresence(): {
  data: PresenceWireItem[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('peer-coordination-presence');
  const query = useQuery({
    queryKey: ['presence-team', 'mine'],
    queryFn: async () => {
      const res = await api.getTeamPresence('mine');
      return res.data;
    },
    enabled: canRead,
    staleTime: 30 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

/**
 * useMySupervisor — Painel V3 Inc 4 («respaldo do supervisor»). Resolve o
 * supervisor primário do caller + presença peer-safe (online/away, metric-free)
 * via `GET /v1/presence/supervisor`. GATEADA por `peer-coordination-presence` no
 * `enabled` (mesma cap do resto da Teia — gatear o fetch, não só o render).
 * `supervisor: null` ⇒ hierarquia não configurada (o card se esconde). Presença
 * é viva: refetch em foco + a cada 60s (barato; sem WS dedicado de supervisor).
 */
export function useMySupervisor(): {
  data: SupervisorRespaldoWire['supervisor'];
  canRead: boolean;
} {
  const canRead = useCapability('peer-coordination-presence');
  const query = useQuery({
    queryKey: ['presence-supervisor'],
    queryFn: () => api.getMySupervisor(),
    enabled: canRead,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
  return { data: query.data?.supervisor ?? null, canRead };
}

export function useUpdateOwnPresence(): ReturnType<
  typeof useMutation<PresenceWireItem, Error, Parameters<typeof api.updateOwnPresence>[0]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.updateOwnPresence>[0]) => api.updateOwnPresence(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['presence-team'] });
    },
  });
}
